import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCurrency } from '../context/CurrencyContext';

const Refund = () => {
  const { t, selectedCountry } = useCurrency();
  const { countryCode } = useParams();
  const isIndia = selectedCountry.code === 'IN';
  const supportEmail = isIndia ? 'support.in@wisedrive.com' : 'support.my@wisedrive.com';
  const supportPhone = isIndia ? '7353 34 35 36' : '+60 122559610';

  // India-specific cancellation & refund policy
  const indiaSections = [
    {
      subHeader: '1. During Initial 10 Days Period',
      description:
        'Every Warranty policy holder / Dealer who has purchased an extended warranty policy from Wisedrive Technologies Private Limited may request the cancellation of the policy and full refund thereof on or before the expiry of 10 (ten) calendar days from the date of receipt of the Warranty policy premium amount. The Warranty Holder/Dealer shall place such policy cancellation and refund request by writing an email to Wisedrive Technologies Private Limited at support.in@wisedrive.com ("Cancellation Request"). Upon receipt of such Cancellation Request within the above-mentioned period, the Warranty policy will be cancelled and the entire policy amount paid will be refunded. The Warranty and all rights under the Warranty Policy shall stand terminated immediately from the date of the Cancellation Request. No claims shall be entertained from the date of the Cancellation Request.',
    },
    {
      subHeader: '2. Cancellation Request Received After Initial 10 Days',
      description:
        'The Warranty Policy Holders are entitled to receipt of refund upon cancellation of the Warranty policy within 10 days from the receipt of premium only. The Warranty Policy Holder shall have the right to place a policy cancellation request after the above-mentioned days; however, for any such Cancellation Request received from the Warranty Policy Holder after this period, no refunds will be processed by Wisedrive Technologies Private Limited.',
    },
    {
      subHeader: '3. Payment and Mode of Refund',
      description:
        'For Cancellation Requests made within 10 days of payment, the refund will be made within 14 (fourteen) business days from the date of receipt of the Cancellation Request. Such refund will be processed through the original mode of payment — i.e., to the payment mode used initially by the Warranty Policy Holder — or Wisedrive Technologies Private Limited may at its discretion process the refund through any other mode upon receipt of specific instruction from the Warranty Policy Holder along with the requisite details.',
    },
  ];

  // Malaysia-specific policy (from inspection terms fees & cancellations section)
  const malaysiaSections = [
    {
      subHeader: 'Inspection Cancellation',
      description: `You have the right to cancel an Inspection and receive a full refund of the Fee, provided the cancellation is made at least 24 hours before the inspection is scheduled. Once the inspection is carried out, no refund will be issued.`,
    },
    {
      subHeader: 'How to Cancel',
      description: `To cancel an Inspection, You must inform Our Service Support Team of your decision to cancel by emailing ${supportEmail} or by calling ${supportPhone}, Monday to Friday 9am to 5pm. To enable us to find Your order, you will need to provide Your name, address, telephone number and booking reference (as provided with your booking confirmation) together with Your cancellation request.`,
    },
    {
      subHeader: 'Warranty Package Cancellation',
      description:
        'The customer can request for cancellation of the Service Inclusive and/or Repair Inclusive Package within two weeks from the date of activation of the package, provided no services have been availed on the car under any scope. No cancellation or refund will be made after the above-stated period.',
    },
    {
      subHeader: 'Total Loss / Write-Off Vehicles',
      description:
        'In the event the vehicle covered under Service Inclusive and/or Repair Inclusive is involved in an accident rendering it beyond economical repair/total loss, Wisedrive Service Maintenance Plan cover will be annulled and no reimbursement will be made for the remaining period of validity. No cancellation request can be processed in such circumstances.',
    },
    {
      subHeader: 'Non-Refundable Items',
      description:
        'Costs for services and repairs that were carried out before the purchase of the packages are not covered or cannot be refunded. The Fee for the Inspection shall remain fully payable where the Customer has failed to ensure proper provision has been made for the Inspection, or where incorrect/incomplete information has prevented Us from carrying out the Inspection.',
    },
  ];

  const sections = isIndia ? indiaSections : malaysiaSections;
  const title = isIndia ? t('legal.refund.titleIN') : t('legal.refund.titleMY');

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar forceWhite hasBanner={false} />

      {/* Hero */}
      <div className="bg-slate-900 pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/${countryCode}`}
            className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('legal.backToHome')}
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-teal-500/20 p-3 rounded-xl">
              <RefreshCw className="h-6 w-6 text-teal-400" />
            </div>
            <span className="text-teal-400 text-sm font-medium uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{title}</h1>
          <p className="text-slate-400 text-lg">{t('legal.refund.subtitle')}</p>
          <p className="text-slate-500 text-sm mt-4">{t('legal.effectiveDate')}: January 1, 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 md:p-12">
            <p className="text-slate-600 mb-10 pb-8 border-b border-slate-100 leading-relaxed">
              {isIndia
                ? 'Wisedrive Technologies Private Limited is committed to fair and transparent refund practices. Please read the following policy carefully before purchasing any of our warranty or service packages.'
                : 'Wisedrive Technologies (M) Sdn. Bhd. Limited maintains a fair cancellation and refund policy for all our inspection and warranty services. Please review the following terms carefully.'}
            </p>

            <div className="space-y-10">
              {sections.map((section, index) => (
                <div key={index} className="pb-8 border-b border-slate-100 last:border-0 last:pb-0">
                  <h2 className="text-xl font-semibold text-slate-900 mb-3">{section.subHeader}</h2>
                  <p className="text-slate-600 leading-relaxed">{section.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick summary card */}
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
            <div className="text-emerald-600 font-semibold mb-1">
              {isIndia ? '✓ Full refund within 10 days' : '✓ Full refund with 24hrs notice'}
            </div>
            <p className="text-slate-600 text-sm">
              {isIndia
                ? 'Cancel your warranty policy within 10 days of purchase for a complete refund.'
                : 'Cancel your inspection booking at least 24 hours in advance to receive a full refund.'}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6">
            <div className="text-amber-600 font-semibold mb-1">
              {isIndia ? '✗ No refund after 10 days' : '✗ No refund after inspection'}
            </div>
            <p className="text-slate-600 text-sm">
              {isIndia
                ? 'Refunds are not available after the 10-day cooling-off period.'
                : 'Once an inspection is carried out, the fee is non-refundable.'}
            </p>
          </div>
        </div>

        {/* Contact Card */}
        <div className="mt-4 bg-teal-50 border border-teal-100 rounded-2xl p-8 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('legal.contactUs')}</h3>
          <p className="text-slate-600 mb-4">
            Need to request a cancellation or refund? Contact our support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`mailto:${supportEmail}`}
              className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-teal-700 transition-colors"
            >
              {supportEmail}
            </a>
            <a
              href={`tel:${supportPhone}`}
              className="inline-flex items-center justify-center gap-2 border border-teal-600 text-teal-700 px-6 py-3 rounded-xl font-medium hover:bg-teal-50 transition-colors"
            >
              {supportPhone}
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Refund;
